import { useTranslate } from "ra-core";
import { useEffect, type CSSProperties } from "react";

import type { StyleTokens } from "../types";
import { contrastText, googleFontsHref, resolveTokens } from "./tokens";

/** Loads the Google Fonts used by a style (once per font set). */
export const useStyleFonts = (fonts: string[]) => {
  const href = googleFontsHref(fonts);
  useEffect(() => {
    if (document.querySelector(`link[data-style-fonts="${href}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.dataset.styleFonts = href;
    document.head.appendChild(link);
  }, [href]);
};

/**
 * A sample clinic CRM screen rendered only with the style tokens (not the app theme),
 * so clients see exactly what their app would look like.
 */
export const StylePreview = ({
  tokens,
  className,
}: {
  tokens?: StyleTokens;
  className?: string;
}) => {
  const translate = useTranslate();
  const t = resolveTokens(tokens);
  const c = t.colors;
  useStyleFonts([t.fonts.heading, t.fonts.body]);
  const s = (key: string) => translate(`hub.styles.sample.${key}`);

  const root: CSSProperties = {
    background: c.background,
    color: c.foreground,
    fontFamily: `"${t.fonts.body}", system-ui, sans-serif`,
    borderRadius: `calc(${t.radius} + 4px)`,
    border: `1px solid ${c.border}`,
  };
  const card: CSSProperties = {
    background: c.surface,
    border: `1px solid ${c.border}`,
    borderRadius: t.radius,
  };
  const heading: CSSProperties = {
    fontFamily: `"${t.fonts.heading}", system-ui, sans-serif`,
  };
  const primaryButton: CSSProperties = {
    background: c.primary,
    color: contrastText(c.primary),
    borderRadius: t.radius,
  };
  const secondaryButton: CSSProperties = {
    background: c.secondary,
    color: contrastText(c.secondary),
    borderRadius: t.radius,
  };
  const badge = (color: string): CSSProperties => ({
    background: `${color}22`,
    color,
    borderRadius: "999px",
  });

  const appointments = [
    ["Lucía Fernández", "09:30", "Logopedia infantil", true],
    ["Marcos Gil", "10:15", "Evaluación inicial", false],
    ["Ana Prieto", "11:00", "Fisioterapia", true],
    ["Javier Ruiz", "12:30", "Revisión", true],
  ] as const;

  return (
    <div
      className={`flex overflow-hidden text-sm ${className ?? ""}`}
      style={root}
    >
      <aside
        className="hidden w-44 shrink-0 flex-col gap-1 p-3 sm:flex"
        style={{ background: c.primary, color: contrastText(c.primary) }}
      >
        <div className="mb-4 flex items-center gap-2 px-2" style={heading}>
          <span
            className="flex size-6 items-center justify-center font-bold"
            style={{
              background: c.accent,
              color: contrastText(c.accent),
              borderRadius: t.radius,
            }}
          >
            C
          </span>
          <span className="font-semibold">Clínica</span>
        </div>
        {["dashboard", "patients", "agenda", "billing"].map((key, i) => (
          <span
            key={key}
            className="px-2 py-1.5"
            style={{
              borderRadius: t.radius,
              background: i === 0 ? `${contrastText(c.primary)}22` : undefined,
              opacity: i === 0 ? 1 : 0.8,
            }}
          >
            {s(key)}
          </span>
        ))}
      </aside>
      <div className="min-w-0 flex-1 space-y-4 p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-lg font-semibold" style={heading}>
            {s("dashboard")}
          </h3>
          <div className="flex items-center gap-2">
            <span
              className="hidden px-3 py-1.5 text-xs md:inline"
              style={{ ...card, color: c.muted }}
            >
              {s("search")}
            </span>
            <span
              className="px-3 py-1.5 text-xs font-medium"
              style={primaryButton}
            >
              + {s("new_appointment")}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            ["appointments_today", "12"],
            ["new_patients", "3"],
            ["revenue", "4.820 €"],
          ].map(([key, value]) => (
            <div key={key} className="p-3" style={card}>
              <p className="text-xs" style={{ color: c.muted }}>
                {s(key)}
              </p>
              <p className="mt-1 text-xl font-semibold" style={heading}>
                {value}
              </p>
            </div>
          ))}
        </div>
        <div className="grid gap-3 md:grid-cols-[1fr_200px]">
          <div className="overflow-hidden" style={card}>
            <p className="px-3 pt-3 font-medium" style={heading}>
              {s("next_appointments")}
            </p>
            <table className="mt-2 w-full text-xs">
              <thead style={{ color: c.muted }}>
                <tr>
                  {["name", "time", "treatment", "status"].map((key) => (
                    <th key={key} className="px-3 py-1.5 text-left font-medium">
                      {s(key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {appointments.map(([name, time, treatment, confirmed]) => (
                  <tr key={name} style={{ borderTop: `1px solid ${c.border}` }}>
                    <td className="px-3 py-1.5">{name}</td>
                    <td className="px-3 py-1.5">{time}</td>
                    <td className="px-3 py-1.5" style={{ color: c.muted }}>
                      {treatment}
                    </td>
                    <td className="px-3 py-1.5">
                      <span
                        className="px-2 py-0.5"
                        style={badge(confirmed ? c.primary : c.accent)}
                      >
                        {s(confirmed ? "confirmed" : "pending")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-2 p-3" style={card}>
            <p className="font-medium" style={heading}>
              {s("new_appointment")}
            </p>
            {[s("name"), s("time")].map((label) => (
              <div key={label} className="space-y-1">
                <p className="text-xs" style={{ color: c.muted }}>
                  {label}
                </p>
                <div
                  className="h-7"
                  style={{
                    border: `1px solid ${c.border}`,
                    borderRadius: t.radius,
                    background: c.background,
                  }}
                />
              </div>
            ))}
            <div className="flex gap-2 pt-1">
              <span
                className="px-3 py-1.5 text-xs font-medium"
                style={primaryButton}
              >
                {s("save")}
              </span>
              <span className="px-3 py-1.5 text-xs" style={secondaryButton}>
                {s("cancel")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Swatches = ({
  tokens,
  className,
}: {
  tokens?: StyleTokens;
  className?: string;
}) => {
  const { colors } = resolveTokens(tokens);
  return (
    <div className={`flex h-10 overflow-hidden ${className ?? ""}`}>
      {(
        ["primary", "secondary", "accent", "background", "foreground"] as const
      ).map((key) => (
        <span
          key={key}
          className="flex-1"
          style={{ background: colors[key] }}
        />
      ))}
    </div>
  );
};
